/**
 * Agent installation — renders a GitAgent package (agent.yaml + SOUL.md + RULES.md)
 * into the framework-native format (`.claude/agents/<name>.md`, `.omp/agents/<name>/`,
 * `.codex/agents/<name>.toml`) and resolves abilities, contracts, fragments, and
 * prompt extensions from the workspace config along the way.
 *
 * System prompt phases (per agent):
 *   1. extends merge     — inherit from parent agent.yaml if manifest.extends is set
 *   2. ability refs      — read SKILL.md frontmatter from installed skills dir
 *   3. contract refs     — locate CONTRACT.md files in the installed skills dir
 *   4. fragments         — resolve framework fragments (agent-mode, handoff, etc.)
 *   5. prompt-extensions — load per-project markdown extensions from skaile.yaml
 *
 * Used by `installer.ts` (the asset-install path) and `skaile install`. Sub-agents
 * declared in `agent.yaml#requires` are installed flat (no nesting) in the same
 * deploy directory.
 */

import { existsSync, readFileSync } from "node:fs";
import { basename, isAbsolute, join, relative, resolve } from "node:path";
import type { DriverTarget, SkWorkspaceConfig } from "@skaile/workspaces/core";
import { DRIVER_TARGETS } from "@skaile/workspaces/core";
import { parse as parseYaml } from "yaml";
import {
  type AbilityRef,
  type ConnectorRef,
  type ContractRef,
  type FragmentContext,
  loadPromptExtensions,
  resolveFragments,
} from "./fragments.js";
import { type AgentManifest, type AgentRenderInput, renderAgentToFramework } from "./renderers.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

function readManifest(agentDir: string): AgentManifest | null {
  const yamlPath = join(agentDir, "agent.yaml");
  if (!existsSync(yamlPath)) return null;
  try {
    return parseYaml(readFileSync(yamlPath, "utf-8")) as AgentManifest;
  } catch {
    return null;
  }
}

function readFile(agentDir: string, name: string): string | null {
  const p = join(agentDir, name);
  if (!existsSync(p)) return null;
  try {
    return readFileSync(p, "utf-8");
  } catch {
    return null;
  }
}

/** Extract YAML frontmatter from a markdown file (content between first two --- delimiters). */
function parseFrontmatter(content: string): Record<string, unknown> {
  const match = content.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return {};
  try {
    return (parseYaml(match[1]!) as Record<string, unknown>) ?? {};
  } catch {
    return {};
  }
}

/**
 * Merge parent and child agent manifests.
 * Scalars: child wins. Arrays: parent + child, deduplicated.
 */
function mergeManifests(parent: AgentManifest, child: AgentManifest): AgentManifest {
  const result: AgentManifest = { ...parent };
  for (const [k, val] of Object.entries(child) as [keyof AgentManifest, unknown][]) {
    if (val === undefined) continue;
    const parentVal = result[k as keyof AgentManifest];
    if (Array.isArray(val) && Array.isArray(parentVal)) {
      (result as Record<string, unknown>)[k] = [...new Set([...parentVal, ...val])];
    } else {
      (result as Record<string, unknown>)[k] = val;
    }
  }
  return result;
}

/**
 * Phase 1: inherit scalars/arrays from a parent agent when `manifest.extends` is set.
 * Returns the (possibly merged) manifest; pushes a warning when the parent is missing.
 */
function applyExtendsMerge(
  manifest: AgentManifest,
  srcDir: string,
  warnings: string[],
): AgentManifest {
  if (!manifest.extends) return manifest;
  const parentDir = resolve(srcDir, manifest.extends);
  const parentManifest = readManifest(parentDir);
  if (parentManifest) {
    return mergeManifests(parentManifest, manifest);
  }
  warnings.push(
    `extends: parent agent not found at "${manifest.extends}" (from ${srcDir}) — skipping inheritance`,
  );
  return manifest;
}

/** Phase 2: resolve ability refs — read description from installed SKILL.md files. */
function resolveAbilityRefs(
  manifest: AgentManifest,
  workspaceRoot: string,
  skillsRelDir: string,
): AbilityRef[] {
  const abilityRefs: AbilityRef[] = [];
  for (const name of manifest.abilities ?? []) {
    const skillMdPath = join(workspaceRoot, skillsRelDir, name, "SKILL.md");
    if (!existsSync(skillMdPath)) continue;
    try {
      const fm = parseFrontmatter(readFileSync(skillMdPath, "utf-8"));
      const desc = typeof fm.description === "string" ? fm.description : undefined;
      if (desc) abilityRefs.push({ name, description: desc });
    } catch {
      // skip unreadable SKILL.md
    }
  }
  return abilityRefs;
}

/** Phase 3: resolve contract refs — locate CONTRACT.md files in the skills dir. */
function resolveContractRefs(
  manifest: AgentManifest,
  workspaceRoot: string,
  skillsRelDir: string,
): ContractRef[] {
  const contractRefs: ContractRef[] = [];
  for (const name of manifest.contracts ?? []) {
    const contractAbsPath = join(workspaceRoot, skillsRelDir, name, "CONTRACT.md");
    if (!existsSync(contractAbsPath)) continue;
    contractRefs.push({ name, relativePath: relative(workspaceRoot, contractAbsPath) });
  }
  return contractRefs;
}

/** Phase 4: assemble the fragment context from the manifest, workspace config, and resolved refs. */
function buildFragmentContext(
  manifest: AgentManifest,
  driverTarget: string,
  skillsRelDir: string,
  wsConfig: SkWorkspaceConfig | undefined,
  abilityRefs: AbilityRef[],
  contractRefs: ContractRef[],
): FragmentContext {
  const subAgentNames = (manifest.requires ?? []).filter((r) => r.name).map((r) => r.name);

  const connectors: ConnectorRef[] = (wsConfig?.connectors ?? []).map((c) => ({
    id: c.id,
    driver: c.driver,
    access: c.access ?? "read-write",
  }));

  const agentsRelDir = DRIVER_TARGETS[driverTarget as DriverTarget]?.local.agent ?? "";

  return {
    framework: driverTarget,
    skillsDir: skillsRelDir,
    agentsDir: agentsRelDir,
    subAgents: subAgentNames,
    connectors,
    abilityRefs,
    contractRefs,
  };
}

// ── Core installation ─────────────────────────────────────────────────────────

/**
 * Install a single GitAgent source directory using the appropriate renderer.
 *
 * When `includeSubAgents` is `true`, recursively installs `requires[]` entries as
 * separate flat agents in the same deploy directory (no nesting). Tracks visited
 * directories to prevent infinite loops in `requires[]` cycles.
 *
 * @param srcDir - Absolute path to the GitAgent source directory (contains `agent.yaml`).
 * @param agentDeployDir - Absolute path to the framework's agent deploy directory.
 * @param workspaceRoot - Absolute path to the workspace root.
 * @param driverTarget - Agent framework ID used to select the renderer.
 * @param skillsRelDir - Relative path to the installed skills dir (e.g. `".claude/skills"`).
 * @param nameOverride - Override name for the installed agent. Defaults to `manifest.name`.
 * @param includeSubAgents - When `true`, recursively install `requires[]` sub-agents.
 * @param visited - Set of resolved source paths already processed (cycle guard).
 * @param wsConfig - Workspace config from `skaile.yaml` (fragments, connectors, extensions).
 * @param log - Optional logger callback for progress messages.
 * @returns Object with `created` file paths and `warnings` list.
 * @docLink packages/asset-manager/concepts#install-agent
 */
export function installAgent(
  srcDir: string,
  agentDeployDir: string,
  workspaceRoot: string,
  driverTarget: string,
  /** Relative path to the installed skills dir (e.g. ".claude/skills") */
  skillsRelDir: string,
  nameOverride: string | undefined,
  includeSubAgents: boolean,
  visited: Set<string>,
  wsConfig: SkWorkspaceConfig | undefined,
  log?: (msg: string) => void,
): { created: string[]; warnings: string[] } {
  const created: string[] = [];
  const warnings: string[] = [];

  const realSrc = resolve(srcDir);
  if (visited.has(realSrc)) return { created, warnings };
  visited.add(realSrc);

  const initialManifest = readManifest(srcDir);
  if (!initialManifest) {
    warnings.push(`${srcDir} has no agent.yaml — skipping (not a valid GitAgent package)`);
    return { created, warnings };
  }

  // Phase 1: extends merge — inherit scalars/arrays from parent agent
  const manifest = applyExtendsMerge(initialManifest, srcDir, warnings);

  const agentName = nameOverride ?? manifest.name ?? basename(srcDir);

  log?.(`Installing git-agent: ${agentName} (${driverTarget})`);

  // Phase 2: resolve ability refs — read description from installed SKILL.md files
  const abilityRefs = resolveAbilityRefs(manifest, workspaceRoot, skillsRelDir);

  // Phase 3: resolve contract refs — locate CONTRACT.md files in the skills dir
  const contractRefs = resolveContractRefs(manifest, workspaceRoot, skillsRelDir);

  // Phase 4: build fragment context and resolve framework fragments + prompt extensions
  const fragmentCtx = buildFragmentContext(
    manifest,
    driverTarget,
    skillsRelDir,
    wsConfig,
    abilityRefs,
    contractRefs,
  );

  const frameworkFragments = resolveFragments(
    fragmentCtx,
    wsConfig?.agent?.fragments,
    workspaceRoot,
  );
  const promptExtensions = loadPromptExtensions(
    wsConfig?.agent?.["prompt-extensions"],
    workspaceRoot,
  );

  const input: AgentRenderInput = {
    manifest,
    soul: readFile(srcDir, "SOUL.md"),
    rules: readFile(srcDir, "RULES.md"),
    duties: readFile(srcDir, "DUTIES.md"),
    agentName,
    agentDeployDir,
    workspaceRoot,
    abilityRefs,
    contractRefs,
    frameworkFragments,
    promptExtensions,
  };

  const result = renderAgentToFramework(input, driverTarget, log);
  created.push(...result.created);
  warnings.push(...result.warnings);

  // Recursively install sub-agents from requires[] as flat top-level agents
  if (includeSubAgents) {
    installSubAgents(manifest, agentName, srcDir, agentDeployDir, workspaceRoot, driverTarget, {
      skillsRelDir,
      includeSubAgents,
      visited,
      wsConfig,
      log,
      created,
      warnings,
    });
  }

  return { created, warnings };
}

/**
 * Recursively install `requires[]` entries as separate flat agents in the same deploy
 * directory. Appends rendered file paths and warnings into the shared `created`/`warnings`
 * arrays carried on `ctx`.
 */
function installSubAgents(
  manifest: AgentManifest,
  agentName: string,
  srcDir: string,
  agentDeployDir: string,
  workspaceRoot: string,
  driverTarget: string,
  ctx: {
    skillsRelDir: string;
    includeSubAgents: boolean;
    visited: Set<string>;
    wsConfig: SkWorkspaceConfig | undefined;
    log?: (msg: string) => void;
    created: string[];
    warnings: string[];
  },
): void {
  for (const req of manifest.requires ?? []) {
    if (!req.source || !req.name) continue;

    const subSrc = isAbsolute(req.source) ? req.source : resolve(srcDir, req.source);

    if (!existsSync(subSrc)) {
      ctx.warnings.push(`Sub-agent source not found: ${req.source} (from ${agentName})`);
      continue;
    }

    const sub = installAgent(
      subSrc,
      agentDeployDir,
      workspaceRoot,
      driverTarget,
      ctx.skillsRelDir,
      undefined, // use sub-agent's own name from its agent.yaml
      ctx.includeSubAgents,
      ctx.visited,
      ctx.wsConfig,
      ctx.log,
    );
    ctx.created.push(...sub.created);
    ctx.warnings.push(...sub.warnings);
  }
}
